// Scoped IndexedDB repository plus a Map-backed memory twin (STORE01/STORE02).
// Holds API JSON only (user state, outbox, catalog/detail/guide/search/artwork metadata): never credentials, tokens,
// provider URLs, media manifests, keys or media bytes.
import { deleteDB, openDB, type DBSchema, type IDBPDatabase, type IDBPTransaction, type StoreNames } from 'idb';
import type { StateOperation } from '@tuned/contracts';

export const SCHEMA_VERSION = 2;
const MiB = 1024 * 1024;
/** A detail record used within this window is "recently used" and evicted after search/catalog. */
export const RECENT_MS = 15 * 60_000;

export type CacheKind = 'catalog' | 'detail' | 'guide' | 'artwork' | 'search';
export type CacheRecord<T = unknown> = {
  key: string; kind: CacheKind; data: T; fetchedAt: number; staleAt: number; sourceRevision: string | null;
  schemaVersion: number; bytes: number; lastUsedAt: number;
};
export type OutboxEntry = {
  operationId: string; baseRevision: number; operation: StateOperation; createdAt: number; notBefore: number; attempts: number;
};
/** Opaque ids only. */
export type Scope = { deviceId: string; profileId: string; connectionId: string | null };

export type Repo = {
  mode: 'indexeddb' | 'memory';
  kvGet<T>(key: string): Promise<T | undefined>;
  kvSet(key: string, value: unknown): Promise<void>;
  outboxAll(): Promise<OutboxEntry[]>;
  outboxPut(e: OutboxEntry): Promise<void>;
  outboxDelete(ids: string[]): Promise<void>;
  cacheGet(key: string): Promise<CacheRecord | undefined>;
  cachePut(rec: CacheRecord): Promise<void>;
  cacheDelete(key: string): Promise<void>;
  /** Drops every cache record (metadata only; never kv or outbox). Returns the deleted keys. */
  cacheClear(): Promise<string[]>;
  cacheBytes(): Promise<number>;
  /** Evicts cache records (never kv or outbox) in STORE01 order until total <= target. Returns the new total. */
  evict(target: number, now: number): Promise<number>;
  close(): void;
};

interface TunedDB extends DBSchema {
  kv: { key: string; value: unknown };
  outbox: { key: string; value: OutboxEntry; indexes: { createdAt: number } };
  cache: { key: string; value: CacheRecord; indexes: { byKindUsed: [string, number] } };
  meta: { key: string; value: number };
}

export const dbName = (s: Scope) => `tuned:${s.deviceId}:${s.profileId}:${s.connectionId ?? 'none'}`;
export const approxBytes = (data: unknown) => (JSON.stringify(data)?.length ?? 0) * 2;
export const isQuotaError = (e: unknown) => (e as { name?: string } | null)?.name === 'QuotaExceededError';

/** STORE01: min(150 MiB, 20% of the origin quota); 25 MiB when unknown. */
export const budgetFor = (quota: number | null | undefined) => (quota && quota > 0 ? Math.min(150 * MiB, Math.floor(quota * 0.2)) : 25 * MiB);
export async function storageBudget(storage: StorageManager | undefined = globalThis.navigator?.storage): Promise<number> {
  try {
    return budgetFor((await storage!.estimate()).quota);
  } catch {
    return budgetFor(null);
  }
}

/** Eviction passes: guide → artwork → detail not used recently → search → catalog → remaining detail. Oldest lastUsedAt first. */
const passes = (now: number): [CacheKind, number][] => [
  ['guide', Infinity], ['artwork', Infinity], ['detail', now - RECENT_MS], ['search', Infinity], ['catalog', Infinity], ['detail', Infinity],
];

type UpgradeTx = IDBPTransaction<TunedDB, StoreNames<TunedDB>[], 'versionchange'>;
/** Ordered, restart-safe steps: each checks before creating, and the whole upgrade is one transaction. */
function migrate(db: IDBPDatabase<TunedDB>, oldVersion: number, tx: UpgradeTx) {
  if (oldVersion < 1) {
    if (!db.objectStoreNames.contains('kv')) db.createObjectStore('kv');
    if (!db.objectStoreNames.contains('outbox')) db.createObjectStore('outbox', { keyPath: 'operationId' }).createIndex('createdAt', 'createdAt');
    if (!db.objectStoreNames.contains('cache')) db.createObjectStore('cache', { keyPath: 'key' });
  }
  if (oldVersion < 2) {
    const cache = tx.objectStore('cache');
    if (!cache.indexNames.contains('byKindUsed')) cache.createIndex('byKindUsed', ['kind', 'lastUsedAt']);
    if (!db.objectStoreNames.contains('meta')) db.createObjectStore('meta');
    void backfillV2(tx);
  }
}
async function backfillV2(tx: UpgradeTx) {
  let total = 0;
  let cur = await tx.objectStore('cache').openCursor();
  while (cur) {
    const r = cur.value as Partial<CacheRecord> & { key: string };
    const fixed = { ...r, kind: r.kind ?? 'catalog', bytes: r.bytes ?? approxBytes(r.data), lastUsedAt: r.lastUsedAt ?? r.fetchedAt ?? 0 } as CacheRecord;
    if (fixed.kind !== r.kind || fixed.bytes !== r.bytes || fixed.lastUsedAt !== r.lastUsedAt) await cur.update(fixed);
    total += fixed.bytes;
    cur = await cur.continue();
  }
  await tx.objectStore('meta').put(total, 'cacheBytes');
}

/** Opens (and migrates) a scoped database. Rejects when unavailable, denied or blocked by an older tab. */
export async function openIdbRepo(name: string, onClosed: () => void = () => {}): Promise<Repo> {
  if (typeof indexedDB === 'undefined') throw new Error('indexeddb-unavailable');
  const db = await new Promise<IDBPDatabase<TunedDB>>((resolve, reject) => {
    let settled = false;
    openDB<TunedDB>(name, SCHEMA_VERSION, {
      upgrade: (d, oldVersion, _v, tx) => migrate(d, oldVersion, tx),
      blocked() { if (!settled) { settled = true; reject(new Error('indexeddb-blocked')); } },
      // Another tab deletes (sign-out, scope removal) or upgrades this database: let it proceed.
      blocking(_c, _b, ev) { (ev.target as IDBDatabase).close(); onClosed(); },
      terminated: onClosed,
    }).then(
      (d) => { if (settled) d.close(); else { settled = true; resolve(d); } },
      (e: unknown) => { if (!settled) { settled = true; reject(e); } },
    );
  });

  async function adjust(key: string, rec: CacheRecord | null) {
    const tx = db.transaction(['cache', 'meta'], 'readwrite');
    const cache = tx.objectStore('cache');
    const meta = tx.objectStore('meta');
    const [old, total] = await Promise.all([cache.get(key), meta.get('cacheBytes')]);
    const next = (total ?? 0) - (old?.bytes ?? 0) + (rec?.bytes ?? 0);
    await Promise.all([rec ? cache.put(rec) : cache.delete(key), meta.put(Math.max(0, next), 'cacheBytes'), tx.done]);
  }

  return {
    mode: 'indexeddb',
    async kvGet<T>(key: string) { return (await db.get('kv', key)) as T | undefined; },
    async kvSet(key, value) { await db.put('kv', value, key); },
    outboxAll: () => db.getAllFromIndex('outbox', 'createdAt'),
    async outboxPut(e) { await db.put('outbox', e); },
    async outboxDelete(ids) {
      if (!ids.length) return;
      const tx = db.transaction('outbox', 'readwrite');
      await Promise.all([...ids.map((id) => tx.store.delete(id)), tx.done]);
    },
    cacheGet: (key) => db.get('cache', key),
    cachePut: (rec) => adjust(rec.key, rec),
    cacheDelete: (key) => adjust(key, null),
    async cacheClear() {
      const tx = db.transaction(['cache', 'meta'], 'readwrite');
      const keys = (await tx.objectStore('cache').getAllKeys()) as string[];
      await Promise.all([tx.objectStore('cache').clear(), tx.objectStore('meta').put(0, 'cacheBytes'), tx.done]);
      return keys;
    },
    async cacheBytes() { return (await db.get('meta', 'cacheBytes')) ?? 0; },
    async evict(target, now) {
      const tx = db.transaction(['cache', 'meta'], 'readwrite');
      const index = tx.objectStore('cache').index('byKindUsed');
      let total = (await tx.objectStore('meta').get('cacheBytes')) ?? 0;
      for (const [kind, maxUsed] of passes(now)) {
        let cur = total > target ? await index.openCursor(IDBKeyRange.bound([kind, -Infinity], [kind, maxUsed])) : null;
        while (cur && total > target) {
          total -= cur.value.bytes;
          await cur.delete();
          cur = await cur.continue();
        }
      }
      total = Math.max(0, total);
      await Promise.all([tx.objectStore('meta').put(total, 'cacheBytes'), tx.done]);
      return total;
    },
    close: () => db.close(),
  };
}

/** Same interface, Maps only: shared device mode, private browsing, denied/blocked IndexedDB or persistent quota errors. */
export function memoryRepo(): Repo {
  const kv = new Map<string, unknown>();
  const outbox = new Map<string, OutboxEntry>();
  const cache = new Map<string, CacheRecord>();
  let bytes = 0;
  return {
    mode: 'memory',
    async kvGet<T>(key: string) { return kv.get(key) as T | undefined; },
    async kvSet(key, value) { kv.set(key, value); },
    async outboxAll() { return [...outbox.values()].sort((a, b) => a.createdAt - b.createdAt); },
    async outboxPut(e) { outbox.set(e.operationId, e); },
    async outboxDelete(ids) { for (const id of ids) outbox.delete(id); },
    async cacheGet(key) { return cache.get(key); },
    async cachePut(rec) { bytes += rec.bytes - (cache.get(rec.key)?.bytes ?? 0); cache.set(rec.key, rec); },
    async cacheDelete(key) { bytes -= cache.get(key)?.bytes ?? 0; cache.delete(key); },
    async cacheClear() { const keys = [...cache.keys()]; cache.clear(); bytes = 0; return keys; },
    async cacheBytes() { return bytes; },
    async evict(target, now) {
      for (const [kind, maxUsed] of passes(now)) {
        const victims = [...cache.values()].filter((r) => r.kind === kind && r.lastUsedAt <= maxUsed).sort((a, b) => a.lastUsedAt - b.lastUsedAt);
        for (const r of victims) {
          if (bytes <= target) return bytes;
          bytes -= r.bytes;
          cache.delete(r.key);
        }
      }
      return bytes;
    },
    close() {},
  };
}

/** Deletes a scoped database. Bounded: a connection that ignores versionchange must not hang sign-out (AUTH05);
 *  the browser completes the queued delete once that connection closes. */
export async function deleteDb(name: string, timeoutMs = 3000): Promise<void> {
  if (typeof indexedDB === 'undefined') return;
  const del = deleteDB(name, { blocked: () => console.warn('tuned: IndexedDB delete blocked by an open connection') }).catch(() => {});
  await Promise.race([del, new Promise<void>((r) => setTimeout(r, timeoutMs))]);
}

/** Every Tuned database on this origin, when the browser can list them. */
export async function tunedDbNames(): Promise<string[]> {
  if (typeof indexedDB === 'undefined' || !indexedDB.databases) return [];
  // Bounded: Chrome can hold databases() while an open/delete on this origin is pending (seen right after playback).
  try {
    const list = await Promise.race([indexedDB.databases(), new Promise<IDBDatabaseInfo[]>((r) => setTimeout(() => r([]), 1500))]);
    return list.map((d) => d.name ?? '').filter((n) => n.startsWith('tuned:'));
  } catch {
    return [];
  }
}
